// Field names here are snake_case, unlike the camelCase used elsewhere in this codebase. That is
// deliberate: this contract is a frozen parity target for the prior Risk Register implementation, and
// the risk-register-parity-*.json fixtures compare against it key for key. Renaming fields would break
// that comparison, so new fields follow snake_case too rather than mixing conventions in one payload.

export type Category =
  | 'reliability'
  | 'techhealth'
  | 'security'
  | 'observability'
  | 'infraops'
  | 'cost'
  | 'delivery'

export type Lane = 'intake' | 'score' | 'cat' | 'own' | 'redate' | 'limbo'

export type Tier = 'critical' | 'high' | 'moderate' | 'unscored'

export type DeliveryState = 'deployed' | 'pending' | 'nochg' | 'gap' | 'discarded'

export const CHG_PRACTICE_WINDOWS = [90, 180, 365] as const

export type ChgPracticeWindow = typeof CHG_PRACTICE_WINDOWS[number]

export function isChgPracticeWindow(value: number): value is ChgPracticeWindow {
  return (CHG_PRACTICE_WINDOWS as readonly number[]).includes(value)
}

export type GroupDimension = 'project' | 'cat' | 'epic' | 'assignee' | 'svp' | 'manager' | 'app'

export type SortOrder = 'worst' | 'score' | 'overdue' | 'idle' | 'age' | 'due' | 'slips' | 'entered' | 'key'

export type TrendWindow = '30d' | '60d' | '90d' | 'ytd' | 'all'

export interface TierDefinition {
  label: string
  short: string
  color: string
}

export interface ChangeRecord {
  actual_end: string | null
  cat: string
  created: string
  resolved: string | null
  status: string
  summary: string
}

export interface ProjectChangePractice {
  chgs: number
  issues: number
}

export interface Row {
  key: string
  was: string | null
  summary: string
  project: string
  type: string
  svp: string
  lead: string | null
  conf: string
  cat: Category | null
  cat_src: 'field' | 'missing'
  epic_key: string | null
  epic: string | null
  status: string
  cat_key: 'done' | 'new' | 'indeterminate'
  open: boolean
  pri: string
  pri_rank: number
  due: string | null
  due_status: 'closed' | 'none' | 'overdue' | 'on-track' | 'at-risk'
  due_days: number | null
  created: string
  age: number
  scc: string | null
  idle: number
  idle_unknown: boolean
  score: number | null
  src: 'measured' | 'missing'
  tier: Tier
  assignee: string | null
  weak: boolean
  resolved: string | null
  dstate: DeliveryState | null
  resolution: string | null
  chgs: string[]
  deployed_on: string | null
  deployed_days: number | null
  releasers: string[]
  mgr: string | null
  mgr_src: string
  mgr_via: string | null
  team_title: string | null
  team_retired: boolean
  own_src: string
  own_conf: string
  app: string | null
  app_n: number
  due_moves: number
  due_slips: number
  due_pulled: number
  due_set: number
  due_cleared: number
  due_last_move: string | null
  due_first: string | null
  due_net_slip: number | null
  asg_moves: number
  d_score: number | null
  d_cat: number | null
  d_asg: number | null
  d_status: number | null
  d_due: number | null
  entered: string
  entered_est: boolean
  entered_days: number | null
  months: number[]
}

export interface PayloadMeta {
  fetched_at: string
  sources: Record<string, {
    duration_ms: number
    cache_hit: boolean
  }>
  fallback: {
    active: boolean
    age_ms: number | null
  }
  caveats: {
    unmapped_projects: number
    missing_risk_scores: number
    missing_risk_categories: number
    missing_managers: number
    unreadable_changes: number
    conservation_drift: number
  }
}

export interface Payload {
  as_of: string
  snapshot: string
  at_risk_days: number
  stale_days: number
  stalled_days: number
  jira: string
  jira_search: string
  non_register_projects: string[]
  categories: Record<Category, string>
  category_order: Category[]
  category_short: Record<Category, string>
  category_colors: Record<Category, string>
  category_question: Record<Category, string>
  svp_order: string[]
  svp_lead: Record<string, string | null>
  unowned_group: string
  no_cat_label: string
  no_manager_label: string
  app_multi: string
  app_none: string
  tiers: Record<Tier, TierDefinition>
  tier_order: Tier[]
  high_score: number
  sharp_score: number
  own_grace: number
  redate_slips: number
  since_default: string
  lane_order: Lane[]
  lane_totals: Record<Lane, number>
  deliv_order: DeliveryState[]
  deliv_labels: Record<DeliveryState, string>
  deliv_short: Record<DeliveryState, string>
  deliv_colors: Record<DeliveryState, string>
  deliv_states: Exclude<DeliveryState, 'discarded'>[]
  deliv_windows: number[]
  deliv_window: string
  deliv_totals: Record<DeliveryState | 'delivered' | 'deployed_in_window', number>
  chg_project: string
  chg_window: ChgPracticeWindow
  chg_active_min: number
  chg_scanned: number
  chg_fetched: string
  chg_unreadable: string[]
  changes: Record<string, ChangeRecord>
  project_changes: Record<string, ProjectChangePractice>
  months: string[]
  month_partial: string
  first_label: string | null
  mbits: {
    reg: number
    open: number
    overdue: number
    resolved: number
    unknown: number
    due_unknown: number
  }
  cortex_ref: string
  apps_by_project: Record<string, string[]>
  rows: Row[]
  untracked: string[]
  meta: PayloadMeta
}

/** Exact shape emitted by the legacy report's `const DATA` assignment. */
export type PayloadSnapshot = Omit<Payload, 'meta'>

export interface ViewState {
  area: string
  group: GroupDimension
  scope: 'open' | 'closed' | 'all'
  due: 'any' | 'overdue' | 'atrisk' | 'soon' | 'nodate'
  idle: 'any' | 'stale' | 'stalled'
  score: 'any' | 'high' | 'measured' | 'unscored'
  owner: 'any' | 'unassigned'
  cat: Category | 'any' | '__nocat__'
  catsrc: 'any' | 'field' | 'missing'
  stage: 'any' | 'todo' | 'prog' | 'limbo'
  q: string
  sort: SortOrder
  win: TrendWindow
  acct: GroupDimension
  unit: string
  duemove: 'any' | 'moved' | 'slipped' | 'repeat' | 'never'
  recent: '14' | '30' | '60' | '90'
  monthby: GroupDimension
  month: string
  pri: 'any' | 'p1' | 'rest'
  lane: Lane | ''
  since: string
  expand: 'auto' | 'all'
  laneall: string
  dwin: string
  deliverby: GroupDimension
}
